import { useEffect, useState } from "react";
import { marked } from "marked";
import { Link } from "react-router-dom";
import { Download, FileText, FileType2, Images, ListChecks, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { SnapshotButton } from "@/components/SnapshotButton";
import { loadGuide, guideTokens, renderMermaid, downloadPdf, downloadDocx, downloadZip } from "@/lib/guideExport";

function Mermaid({ code, i }) {
  const [svg, setSvg] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { renderMermaid(code).then(setSvg).catch((e) => setErr(String(e?.message || e))); }, [code]);
  if (err) return <pre className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid={`guide-diagram-error-${i}`}>{err}</pre>;
  if (!svg) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return <div className="guide-diagram my-6 overflow-x-auto rounded-xl border border-slate-200 bg-white p-4" data-testid={`guide-diagram-${i}`} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export default function ProcessGuide() {
  const { can } = useAuth();
  const [tokens, setTokens] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { loadGuide().then((md) => setTokens(guideTokens(md))); }, []);
  const run = (kind, fn) => async () => {
    setBusy(kind);
    try { await fn(tokens); toast.success("Download ready"); } catch (e) { toast.error(`Could not build the ${kind}: ${e?.message || e}`); } finally { setBusy(null); }
  };
  let d = 0;
  return (
    <div className="space-y-5" data-testid="process-guide-page">
      <PageHeader title="Process guide" subtitle="How EUDR applies to EP Group's trade flows, with flow charts. Download it as a branded PDF or Word document.">
        <Button data-testid="guide-download-pdf" disabled={!tokens || !!busy} onClick={run("PDF", downloadPdf)}>{busy === "PDF" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}Download PDF</Button>
        <Button variant="outline" data-testid="guide-download-docx" disabled={!tokens || !!busy} onClick={run("Word document", downloadDocx)}>{busy === "Word document" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileType2 className="mr-2 h-4 w-4" />}Download Word (.docx)</Button>
        <Button variant="outline" data-testid="guide-download-png" disabled={!tokens || !!busy} onClick={run("flow chart zip", downloadZip)}>{busy === "flow chart zip" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Images className="mr-2 h-4 w-4" />}Download flow charts (PNG)</Button>
        <SnapshotButton testid="guide-download-snapshot" />
        {can.admin && <Button asChild variant="ghost" data-testid="guide-open-plan"><Link to="/plan"><ListChecks className="mr-2 h-4 w-4" />First 2 weeks plan</Link></Button>}
      </PageHeader>
      {!tokens ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <article className="guide-prose max-w-5xl rounded-2xl border border-slate-200 bg-white px-8 py-8 shadow-sm" data-testid="guide-content">
          {tokens.map((t, i) => (t.type === "code" && t.lang === "mermaid"
            ? <Mermaid key={i} code={t.text} i={++d} />
            : <div key={i} dangerouslySetInnerHTML={{ __html: marked.parser([t]) }} />))}
        </article>
      )}
      <p className="flex items-center gap-1.5 text-xs text-slate-400"><Download className="h-3.5 w-3.5" />Files are built in your browser from the same text and diagrams shown here.</p>
    </div>
  );
}
